import { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const useLightboxKeyboard = ({ onClose, onNext, onPrev }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNext, onPrev]);
};

function LightboxNavButton({ label, onClick, position, testId, children }) {
  const stopAnd = useCallback(
    (e) => {
      e.stopPropagation();
      onClick();
    },
    [onClick],
  );
  return (
    <button
      onClick={stopAnd}
      className={`absolute ${position} text-paper/60 hover:text-paper transition-colors p-2`}
      aria-label={label}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  useLightboxKeyboard({ onClose, onNext, onPrev });
  const photo = photos[index];
  if (!photo) return null;

  const stopClick = (e) => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 z-50 lightbox-backdrop flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox"
    >
      <LightboxNavButton
        label="Chiudi"
        onClick={onClose}
        position="top-5 right-5"
        testId="lightbox-close"
      >
        <X className="w-8 h-8" />
      </LightboxNavButton>

      <LightboxNavButton
        label="Precedente"
        onClick={onPrev}
        position="left-4 sm:left-8"
        testId="lightbox-prev"
      >
        <ChevronLeft className="w-10 h-10" />
      </LightboxNavButton>

      <img
        src={photo.src}
        alt={photo.alt || ""}
        onClick={stopClick}
        className="max-w-[92vw] max-h-[82vh] object-contain shadow-2xl"
      />

      {photo.quote && (
        <p
          onClick={stopClick}
          className="absolute bottom-14 left-1/2 -translate-x-1/2 font-display italic text-paper/90 text-base sm:text-lg max-w-2xl text-center px-6"
          data-testid="lightbox-quote"
        >
          «{photo.quote}»
        </p>
      )}

      <LightboxNavButton
        label="Successiva"
        onClick={onNext}
        position="right-4 sm:right-8"
        testId="lightbox-next"
      >
        <ChevronRight className="w-10 h-10" />
      </LightboxNavButton>

      <div className="absolute bottom-5 left-0 right-0 text-center font-mono text-xs tracking-[0.3em] uppercase text-paper/60">
        {index + 1} / {photos.length}
      </div>
    </div>
  );
}

export default Lightbox;
